import React, { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Button,
    Alert,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import { DataGrid } from "@mui/x-data-grid";

import { useRangos } from "../hooks";
import { generateRangoColumns } from "../helpers/columns";
import { RangosMobileList, ModalEliminarRango } from "../components";
import {
    DATA_GRID_SX,
    DATA_GRID_SLOT_PROPS,
    DATA_GRID_LOCALE_TEXT,
    DATA_GRID_INITIAL_STATE,
    DATA_GRID_PAGE_SIZE_OPTIONS,
} from "../../../components/ui/dataGridStyles";
import { BOTON_PRIMARIO_SX } from "../../../components/ui/buttonStyles";
import { StateContext } from "../../../components/context/StateProvider";
import Toasts from "../../../components/notificacion/Toasts";

/**
 * Página de listado de rangos de precios (DataGrid en escritorio, tarjetas en
 * móvil). Orquestador: useRangos aporta los datos y el borrado,
 * helpers/columns.js las columnas de escritorio, RangosMobileList la vista
 * móvil y ModalEliminarRango la confirmación de borrado; el alta/edición ya
 * no es un modal de esta página (ver TASK-009): navega a RangoFormPage
 * (/rangos/nuevo y /rangos/:id/editar).
 *
 * Control de acceso: el rol ADMIN requerido para esta página se resuelve en
 * src/routes.js (roles: [Roles.ADMIN.role]), NO aquí. Envolver también este
 * componente en RequierePermisos duplicaría la protección en dos capas.
 */
const ListaRangos = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const navigate = useNavigate();
    const { state, updateState } = useContext(StateContext);

    const { rangos, transportistas, loading, error, eliminarRango } = useRangos();

    // Estado de apertura del modal de confirmación de borrado (TASK-010).
    // onEliminar recibe la fila completa (firma unificada entre DataGrid y
    // RangosMobileList, ver helpers/columns.js). `eliminando` es el loading
    // LOCAL de la mutación: useRangos.loading solo cubre la carga inicial de
    // rangos+transportistas (ver hooks/useRangos.js), nunca las mutaciones.
    const [modalEliminar, setModalEliminar] = useState({ open: false, rango: null });
    const [eliminando, setEliminando] = useState(false);

    // Mientras haya un modal de borrado abierto se deshabilita el botón de
    // añadir, para no permitir dos flujos de mutación simultáneos
    const hayModalAbierto = modalEliminar.open;

    // El alta ya no abre un modal: navega a la página de formulario (alta)
    const handleAbrirCrear = useCallback(() => {
        navigate("/rangos/nuevo");
    }, [navigate]);

    // onEditar/onEliminar reciben la fila completa: misma firma en DataGrid
    // (helpers/columns.js) y en RangosMobileList, así que se pasa el mismo
    // handler a las dos vistas sin envoltorios. Editar ya no abre un modal:
    // navega a la página de formulario (edición) pasando la fila en el state,
    // para que RangoFormPage no tenga que recuperarla con RangosService.list()
    const handleEditar = useCallback((fila) => {
        navigate(`/rangos/${fila.id}/editar`, { state: { rango: fila } });
    }, [navigate]);

    const handleEliminar = useCallback((fila) => {
        setModalEliminar({ open: true, rango: fila });
    }, []);

    // ARRASTRE DE TASK-007 (reviewer): el prop `loading` que se pasaba a
    // DataGrid/RangosMobileList era siempre false por construcción (vive
    // dentro del "else" de la carga inicial, ver más abajo). DECISIÓN: se
    // elimina en vez de sustituirlo por `eliminando`. ModalEliminarRango ya
    // muestra su propio CircularProgress + "Eliminando..." en el botón y
    // bloquea la interacción con su Dialog mientras dura el borrado; añadir
    // además un overlay de carga en la tabla que queda debajo sería una señal
    // de carga duplicada y redundante para la misma operación.
    const handleCerrarModalEliminar = useCallback(() => {
        if (!eliminando) setModalEliminar({ open: false, rango: null });
    }, [eliminando]);

    const handleConfirmarEliminar = useCallback(async () => {
        setEliminando(true);
        try {
            await eliminarRango(modalEliminar.rango.id);
            updateState({
                toasts: [...state.toasts, Toasts.generateToast("Rango eliminado correctamente", "success")],
            });
            setModalEliminar({ open: false, rango: null });
        } catch (err) {
            updateState({
                toasts: [...state.toasts, Toasts.generateToast(err.message || "No se pudo eliminar el rango de precios.", "error")],
            });
        } finally {
            setEliminando(false);
        }
    }, [eliminarRango, modalEliminar.rango, state.toasts, updateState]);

    const columns = useMemo(
        () => generateRangoColumns(transportistas, { onEditar: handleEditar, onEliminar: handleEliminar }),
        [transportistas, handleEditar, handleEliminar]
    );

    const mensajeError = error.rangos
        ? "No se pudieron cargar los rangos de precios. Intenta nuevamente."
        : error.transportistas
            ? "No se pudieron cargar los transportistas. Los rangos se muestran sin nombre de transportista."
            : null;

    return (
        <Box id="lista-rangos" sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                {/* Header */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            justifyContent: "space-between",
                            alignItems: { xs: "stretch", sm: "flex-start" },
                            gap: 2,
                            mb: 0.5,
                        }}
                    >
                        <Box>
                            <Typography
                                variant={isMobile ? "h6" : "h5"}
                                sx={{
                                    fontWeight: 700,
                                    color: "#212529",
                                    mb: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <PriceChangeIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                                Rangos de precios
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#6c757d" }}>
                                Consulta y gestiona los rangos de precios por transportista
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAbrirCrear}
                            disabled={hayModalAbierto}
                            fullWidth={isMobile}
                            sx={BOTON_PRIMARIO_SX}
                        >
                            Añadir rango de precios
                        </Button>
                    </Box>
                </Box>

                {/* Contenido */}
                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>
                    {mensajeError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {mensajeError}
                        </Alert>
                    )}

                    {!isMobile ? (
                        <DataGrid
                            rows={rangos}
                            columns={columns}
                            localeText={DATA_GRID_LOCALE_TEXT}
                            autoHeight
                            loading={loading}
                            disableRowSelectionOnClick
                            initialState={DATA_GRID_INITIAL_STATE}
                            pageSizeOptions={DATA_GRID_PAGE_SIZE_OPTIONS}
                            slotProps={DATA_GRID_SLOT_PROPS}
                            sx={DATA_GRID_SX}
                        />
                    ) : (
                        <RangosMobileList
                            rows={rangos}
                            loading={loading}
                            transportistas={transportistas}
                            onEditar={handleEditar}
                            onEliminar={handleEliminar}
                        />
                    )}
                </Box>
            </Card>

            <ModalEliminarRango
                show={modalEliminar.open}
                onHide={handleCerrarModalEliminar}
                onConfirm={handleConfirmarEliminar}
                rango={modalEliminar.rango}
                loading={eliminando}
            />
        </Box>
    );
};

export default ListaRangos;
